import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { UNIVERSAL_CACHE_KEYS } from './cache-keys';

interface BulkStorageImportParams {
  itemIds: string[];
  categoryId?: string;
}

/**
 * Generates display order for new storage items
 * Places new items at the beginning for better UX
 */
const generateDisplayOrder = () => {
  return Date.now(); // Simple timestamp-based ordering
};

/**
 * Hook for bulk importing images to Storage
 */
export const useBulkStorageImageImport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ itemIds, categoryId }: BulkStorageImportParams) => {
      if (itemIds.length === 0) {
        throw new Error('No images selected for import');
      }

      console.log('[BulkStorageImageImport] Importing images to storage:', {
        imageCount: itemIds.length,
        imageIds: itemIds,
        categoryId
      });

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Prepare storage items for bulk insert using foreign key columns
      const storageItems = itemIds.map(itemId => ({
        user_id: user.id,
        content_type: 'private_image',
        workbook_image_id: itemId, // Use foreign key column instead of item_id
        category_id: categoryId || null,
        display_order: generateDisplayOrder()
      }));

      // Bulk insert into universal_storage_items
      const { data, error } = await supabase
        .from('universal_storage_items')
        .insert(storageItems)
        .select('id, workbook_image_id, content_type');

      if (error) {
        console.error('[BulkStorageImageImport] Error inserting to storage:', error);
        throw error;
      }

      console.log('[BulkStorageImageImport] Successfully imported images to storage:', data);
      return data;
    },
    onSuccess: (data) => {
      // Comprehensive cache invalidation for images
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS });
      
      // Invalidate all filtered storage queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'universalStorageItems';
        }
      });

      // Force refetch of the Universal Library data immediately
      queryClient.refetchQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      
      // Show success toast
      toast({
        title: "Images Added to Storage",
        description: `${data.length} image${data.length === 1 ? '' : 's'} added to storage successfully.`,
        variant: "default",
      });
    },
    onError: (error: Error) => {
      console.error('[BulkStorageImageImport] Import failed:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to add images to storage. Please try again.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook for bulk importing research to Storage
 */
export const useBulkStorageResearchImport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ itemIds, categoryId }: BulkStorageImportParams) => {
      if (itemIds.length === 0) {
        throw new Error('No research selected for import');
      }

      console.log('[BulkStorageResearchImport] Importing research to storage:', {
        researchCount: itemIds.length,
        researchIds: itemIds,
        categoryId
      });

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Prepare storage items for bulk insert
      const storageItems = itemIds.map(itemId => ({
        user_id: user.id,
        content_type: 'private_research',
        workbook_research_id: itemId, // Use foreign key column
        category_id: categoryId || null,
        display_order: generateDisplayOrder()
      }));

      // Bulk insert into universal_storage_items
      const { data, error } = await supabase
        .from('universal_storage_items')
        .insert(storageItems)
        .select('id, workbook_research_id, content_type');

      if (error) {
        console.error('[BulkStorageResearchImport] Error inserting to storage:', error);
        throw error;
      }

      console.log('[BulkStorageResearchImport] Successfully imported research to storage:', data);
      return data;
    },
    onSuccess: (data) => {
      // Comprehensive cache invalidation for research
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS });
      
      // Invalidate all filtered storage queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'universalStorageItems';
        }
      });

      // Force refetch of the Universal Library data immediately
      queryClient.refetchQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      
      // Show success toast
      toast({
        title: "Research Added to Storage",
        description: `${data.length} research item${data.length === 1 ? '' : 's'} added to storage successfully.`,
        variant: "default",
      });
    },
    onError: (error: Error) => {
      console.error('[BulkStorageResearchImport] Import failed:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to add research to storage. Please try again.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook for bulk importing pages/documents to Storage
 */
export const useBulkStoragePageImport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ itemIds, categoryId }: BulkStorageImportParams) => {
      if (itemIds.length === 0) {
        throw new Error('No documents selected for import');
      }

      console.log('[BulkStoragePageImport] Importing documents to storage:', {
        pageCount: itemIds.length,
        pageIds: itemIds,
        categoryId
      });

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Prepare storage items for bulk insert
      // Fixed: Pages should use 'private_page' to match the pattern of other private content types
      const storageItems = itemIds.map(itemId => ({
        user_id: user.id,
        content_type: 'private_page',
        workbook_page_id: itemId, // Use foreign key column
        category_id: categoryId || null,
        display_order: generateDisplayOrder()
      }));

      // Bulk insert into universal_storage_items
      const { data, error } = await supabase
        .from('universal_storage_items')
        .insert(storageItems)
        .select('id, workbook_page_id, content_type');

      if (error) {
        console.error('[BulkStoragePageImport] Error inserting to storage:', error);
        throw error;
      }

      console.log('[BulkStoragePageImport] Successfully imported documents to storage:', data);
      return data;
    },
    onSuccess: (data) => {
      // Comprehensive cache invalidation for documents
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES });
      queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS });
      
      // Invalidate all filtered storage queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'universalStorageItems';
        }
      });

      // Force refetch of the Universal Library data immediately
      queryClient.refetchQueries({ queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_DATA });
      
      // Show success toast
      toast({
        title: "Documents Added to Storage",
        description: `${data.length} document${data.length === 1 ? '' : 's'} added to storage successfully.`,
        variant: "default",
      });
    },
    onError: (error: Error) => {
      console.error('[BulkStoragePageImport] Import failed:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to add documents to storage. Please try again.",
        variant: "destructive",
      });
    },
  });
}; 